import React, { MouseEvent, ReactNode, useCallback } from 'react';
import { makeStyles } from 'tss-react/mui';
import { IconButton } from '@ui/buttons';
import { SxProps } from '@mui/material';
import { useBooleanState } from '@client/hooks/use-utils.hooks';

type HoverDeleteButtonProps = {
  children: ReactNode;
  onDelete?: (event: MouseEvent<HTMLButtonElement>) => void;
  isHovered?: boolean;
  deleteIcon?: 'Close' | 'Delete';
  disabled?: boolean;
  'aria-label'?: string;
  className?: string;
};

const useStyles = makeStyles()((theme) => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    // Keep a hit area large enough for the overlay IconButton (a caption
    // number is otherwise only a few pixels wide and the icon collapses).
    minWidth: theme.spacing(3),
    minHeight: theme.spacing(3),
  },
  content: {
    transition: 'opacity 0.2s ease',
  },
  contentHidden: {
    opacity: 0,
  },
  deleteButton: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0,
    transition: 'opacity 0.2s ease',
    pointerEvents: 'none',
    '& button': {
      flexShrink: 0,
      pointerEvents: 'auto',
    },
  },
  deleteButtonVisible: {
    opacity: 1,
  },
}));

/**
 * A container that shows its children by default, and shows a delete button on hover.
 * Hover detection can be controlled externally via `isHovered` prop, or internally if not provided.
 */
export const HoverDeleteButton: React.FC<HoverDeleteButtonProps> = ({
  children,
  onDelete,
  isHovered: externalHovered,
  deleteIcon = 'Close',
  disabled = false,
  'aria-label': ariaLabel = 'Delete',
  className,
}) => {
  const { classes, cx } = useStyles();
  const [internalHovered, beginInternalHover, endInternalHover] =
    useBooleanState(false);

  // Use external hover state if provided, otherwise use internal state
  const isHovered = externalHovered ?? internalHovered;

  const handleDeleteClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      if (disabled) return;
      event.stopPropagation();
      onDelete?.(event);
    },
    [onDelete, disabled],
  );

  return (
    <div
      className={cx(classes.container, className)}
      onMouseEnter={beginInternalHover}
      onMouseLeave={endInternalHover}
    >
      <div
        className={cx(
          classes.content,
          isHovered && !disabled && classes.contentHidden,
        )}
      >
        {children}
      </div>
      <div
        className={cx(
          classes.deleteButton,
          isHovered && !disabled && classes.deleteButtonVisible,
        )}
      >
        <IconButton
          category="mui"
          icon={deleteIcon}
          shape="square"
          size="small"
          sx={BUTTON_SX}
          onClick={handleDeleteClick}
          disabled={disabled || !onDelete}
          aria-label={ariaLabel}
        />
      </div>
    </div>
  );
};

const BUTTON_SX = {
  color: 'text.secondary',
  '&:hover': {
    color: 'rgba(0, 0, 0, 1)',
  },
} as const satisfies SxProps;
